/** Shared grant helpers for validation rules, sharing, list view visibility. */

export function grantLookupMeta(targetType) {
  switch (String(targetType || '').toUpperCase()) {
    case 'ROLE':
      return {
        objectName: 'role',
        searchFields: 'name',
        displayFields: 'name',
        placeholder: 'Search roles…',
      };
    case 'TEAM':
      return {
        objectName: 'user_group',
        searchFields: 'name,description',
        displayFields: 'name',
        placeholder: 'Search user groups…',
      };
    case 'USER':
    default:
      return {
        objectName: 'user',
        searchFields: 'firstname,lastname,email,username',
        displayFields: 'firstname,lastname',
        placeholder: 'Search users…',
      };
  }
}

export function normalizeContextTargetType(t) {
  const u = String(t || 'USER').toUpperCase();
  if (u === 'ROLE' || u === 'TEAM' || u === 'USER') return u;
  return 'USER';
}

export function grantTypeShortLabel(type) {
  switch (String(type || '').toUpperCase()) {
    case 'ROLE':
      return 'Role';
    case 'TEAM':
      return 'Group';
    case 'USER':
    default:
      return 'User';
  }
}

export function contextGrantChipLabel(g) {
  const label = String(g.target_label || '').trim() || g.target_id;
  return `${grantTypeShortLabel(g.target_type)} · ${label}`;
}

export function getContextGrantChipBadgeProps(targetType) {
  const t = normalizeContextTargetType(targetType);
  const base =
    'd-inline-flex align-items-center rounded-1 ps-2 pe-1 py-1 fw-normal border-0';
  if (t === 'ROLE') {
    return { bg: 'success', className: base };
  }
  if (t === 'TEAM') {
    return { bg: 'warning', text: 'dark', className: base };
  }
  return { bg: 'primary', className: base };
}

export function dedupeGrants(list) {
  const seen = new Set();
  const out = [];
  for (const g of list || []) {
    const id = g?.target_id != null ? String(g.target_id).trim() : '';
    if (!id) continue;
    const tt = normalizeContextTargetType(g.target_type);
    const k = `${tt}:${id}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({
      target_type: tt,
      target_id: id,
      target_label: g.target_label != null ? String(g.target_label) : '',
    });
  }
  return out;
}

export function parseContextGrants(raw) {
  if (raw == null) return [];
  let o = raw;
  if (typeof o === 'string' && o.trim()) {
    try {
      o = JSON.parse(o);
    } catch {
      return [];
    }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) {
    return [];
  }
  const list = o.grants || o.access;
  if (!Array.isArray(list) || !list.length) {
    return [];
  }
  return dedupeGrants(
    list.map((g) => ({
      target_type: normalizeContextTargetType(g.target_type),
      target_id: g.target_id != null ? String(g.target_id) : '',
      target_label: g.target_label != null ? String(g.target_label) : '',
    }))
  );
}

export function buildContextPayload(grants) {
  const rows = dedupeGrants(grants);
  if (rows.length === 0) return null;
  return {
    grants: rows.map((r) => {
      const g = { target_type: r.target_type, target_id: r.target_id };
      if (r.target_label) g.target_label = r.target_label;
      return g;
    }),
  };
}

export function isAllUsersContext(raw) {
  if (raw == null) return false;
  let o = raw;
  if (typeof o === 'string' && o.trim()) {
    try {
      o = JSON.parse(o);
    } catch {
      return false;
    }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) {
    return false;
  }
  const scope = String(o.scope || o.visibility || '').toLowerCase();
  return scope === 'all_users' || scope === 'all' || scope === 'public';
}

export function isCreatorOnlyContext(raw) {
  if (raw == null) return false;
  let o = raw;
  if (typeof o === 'string' && o.trim()) {
    try {
      o = JSON.parse(o);
    } catch {
      return false;
    }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) {
    return false;
  }
  const scope = String(o.scope || o.visibility || '').toLowerCase();
  return scope === 'creator_only' || scope === 'me_only';
}

export function buildCreatorOnlyContext(userId = null) {
  const uid = userId != null ? String(userId).trim() : '';
  if (uid) {
    return { scope: 'creator_only', user_id: uid };
  }
  return { scope: 'creator_only' };
}

export function parseListViewVisibilityMode(raw) {
  if (isCreatorOnlyContext(raw)) {
    return 'me_only';
  }
  if (hasRestrictedGrants(raw)) {
    return 'restricted';
  }
  if (isAllUsersContext(raw)) {
    return 'all';
  }
  return 'all';
}

export function buildListViewContextPayload(visibilityMode, grantsOrContext, ownerUserId = null) {
  if (visibilityMode === 'me_only') {
    return buildCreatorOnlyContext(ownerUserId);
  }
  if (visibilityMode === 'restricted') {
    const grants = Array.isArray(grantsOrContext)
      ? grantsOrContext
      : parseContextGrants(grantsOrContext);
    return buildContextPayload(grants);
  }
  return { scope: 'all_users' };
}

export function hasRestrictedGrants(raw) {
  return parseContextGrants(raw).length > 0;
}
